class CartDrawer extends Theme.Component {
  static recommendations = new Map();

  constructor() {
    super();

    this.copy_swaps = new Set();
  }

  connectedCallback() {
    super.connectedCallback();

    this.addDirectEvent("addtocart", document, () => {
      this.render()
        .catch((error) => {
          console.error("CartDrawer render failed", error);
        })
        .then(() => {
          this.show();

          if (this.limit_flagged) {
            this.limit_flagged = false;
            this.flashLimit();
          }
        });
    });

    this.addDirectEvent("cartlink:click", document, () => {
      this.show();
    });

    if (Theme.URLParams.has("cart")) {
      this.show();
    }

    this.addDirectEvent("click", document, (event) => {
      if (!event.target.closest(".cart-item-frequency")) {
        this.closeFrequencies();
      }
    });

    this.addDirectEvent("keydown", document, (event) => {
      if (event.key === "Escape") {
        this.closeFrequencies();
      }
    });

    this.loadUpsells();
    this.enforceLimit().then(() => this.replaceSubscriptionCopies());
  }

  pendingCopySwaps() {
    return Array.from(this.querySelectorAll("[data-copy-swap]"))
      .map((line) => JSON.parse(line.dataset.copySwap))
      .filter((swap) => !this.copy_swaps.has(swap.key));
  }

  replaceSubscriptionCopies() {
    const swaps = this.pendingCopySwaps();

    if (swaps.length === 0) {
      return Promise.resolve();
    }

    return this.guard(() => {
      swaps.forEach((swap) => this.copy_swaps.add(swap.key));

      return swaps
        .reduce((chain, swap) => chain.then(() => this.replaceCopy(swap)), Promise.resolve())
        .then(() => this.syncCart())
        .catch((error) => {
          console.error("CartDrawer sync failed", error);
        });
    });
  }

  replaceCopy(swap) {
    return this.findLine(swap.key)
      .then((line) => this.swapLine(line, {
        id: swap.id,
        quantity: swap.quantity,
        properties: line.properties || {}
      }))
      .catch((error) => {
        console.error("CartDrawer copy swap failed", error);
      });
  }

  get itemLimit() {
    return parseInt(this.dataset.itemLimit, 10) || 0;
  }

  get itemCount() {
    const panel = this.querySelector("#cart");

    return panel ? parseInt(panel.dataset.itemCount, 10) || 0 : 0;
  }

  get roomLeft() {
    if (this.itemLimit < 1) {
      return Infinity;
    }

    return Math.max(this.itemLimit - this.itemCount, 0);
  }

  markLimitFlash() {
    this.limit_flagged = true;
  }

  flashLimit(message) {
    const notice = this.querySelector(".cart-limit-notice");

    if (!notice) {
      return;
    }

    if (message) {
      notice.querySelector(".cart-limit-notice-text").textContent = message;
    }

    notice.hidden = false;

    notice.classList.remove("state--flash");
    void notice.offsetWidth;
    notice.classList.add("state--flash");
  }

  refuseAdd(message) {
    this.show();
    this.flashLimit(message);
  }

  handleCartError(error) {
    if (error && error.status === 422) {
      this.refuseAdd(error.description || error.message);

      return true;
    }

    return false;
  }

  enforceLimit() {
    if (this.itemLimit < 1) {
      return Promise.resolve();
    }

    return fetch("/cart.js")
      .then((response) => response.json())
      .then((cart) => this.trimToLimit(cart))
      .catch((error) => {
        console.error("CartDrawer limit check failed", error);
      });
  }

  countedItems(cart) {
    return cart.items.reduce((total, item) => {
      return item.final_price === 0 ? total : total + item.quantity;
    }, 0);
  }

  trimToLimit(cart) {
    let excess = this.countedItems(cart) - this.itemLimit;

    if (excess < 1) {
      return Promise.resolve();
    }

    const reductions = [];

    for (let index = 0; index < cart.items.length && excess > 0; index++) {
      const item = cart.items[index];

      if (item.final_price === 0) {
        continue;
      }

      const reduction = Math.min(excess, item.quantity);

      reductions.push({ id: item.key, quantity: item.quantity - reduction });
      excess -= reduction;
    }

    return reductions
      .reduce((chain, reduction) => chain.then(() => this.changeLine(reduction)), Promise.resolve())
      .then((trimmed) => {
        document.dispatchEvent(new CustomEvent("cart:change", {
          detail: trimmed
        }));

        return this.render();
      })
      .then(() => this.flashLimit());
  }

  show() {
    document.dispatchEvent(new CustomEvent("cartdrawer:show"));

    Theme.URLParams.set("cart", "open");

    setTimeout(() => {
      this.classList.add("state--active");
      this.returnFocus = document.activeElement;

      const panel = this.querySelector("#cart");
      panel?.setAttribute("tabindex", "-1");

      panel?.addEventListener("transitionend", () => panel.focus({ preventScroll: true }), { once: true });
      setTimeout(() => panel?.focus({ preventScroll: true }), 400);
    }, 50);
  }

  hide() {
    this.classList.remove("state--active");

    Theme.URLParams.delete("cart");
    this.returnFocus?.focus?.({ preventScroll: true });
    this.returnFocus = null;
  }

  openFrequency(detail, element) {
    const frequency = element.closest(".cart-item-frequency");
    const options = frequency.querySelector(".cart-item-frequency-options");
    const open = !frequency.classList.contains("state--open");

    this.closeFrequencies();

    if (!open) {
      return;
    }

    const room = this.getBoundingClientRect().bottom - element.getBoundingClientRect().bottom;

    frequency.classList.toggle("state--above", room < options.scrollHeight + 40);
    frequency.classList.add("state--open");
    options.hidden = false;
    element.setAttribute("aria-expanded", "true");

    const selected = options.querySelector(".state--selected") || options.querySelector(".cart-item-frequency-option");

    if (selected) {
      selected.focus();
    }
  }

  closeFrequencies() {
    this.querySelectorAll(".cart-item-frequency.state--open").forEach((frequency) => {
      frequency.classList.remove("state--open", "state--above");
      frequency.querySelector(".cart-item-frequency-options").hidden = true;
      frequency.querySelector(".cart-item-frequency-trigger").setAttribute("aria-expanded", "false");
    });
  }

  guard(task) {
    if (this.pending) {
      return Promise.resolve();
    }

    this.pending = true;
    this.classList.add("state--pending");
    this.setAttribute("aria-busy", "true");

    return Promise.resolve()
      .then(task)
      .finally(() => {
        this.pending = false;
        this.classList.remove("state--pending");
        this.removeAttribute("aria-busy");
      });
  }

  changeSubscription(detail, element) {
    return this.guard(() => {
      const frequency = element.closest(".cart-item-frequency");

      this.closeFrequencies();
      frequency.classList.add("state--loading");

      return this.updateLine({
        id: detail.key,
        quantity: detail.quantity,
        selling_plan: detail.selling_plan
      }, element).catch(() => {
        frequency.classList.remove("state--loading");
      });
    });
  }

  changeIntoSubscription(detail, element) {
    return this.guard(() => {
      element.classList.add("state--loading");

      return this.findLine(detail.key)
        .then((line) => this.swapLine(line, {
          id: detail.id,
          quantity: detail.quantity,
          selling_plan: detail.selling_plan
        }))
        .catch((error) => {
          element.classList.remove("state--loading");

          if (!this.handleCartError(error)) {
            console.error("CartDrawer subscribe failed", error);
          }
        })
        .then(() => this.syncCart())
        .catch((error) => {
          console.error("CartDrawer sync failed", error);
        });
    });
  }

  findLine(key) {
    return fetch("/cart.js")
      .then((response) => response.json())
      .then((cart) => {
        const line = cart.items.find((item) => item.key === key);

        return line || Promise.reject(new Error(`Cart line ${key} not found`));
      });
  }

  swapLine(line, replacement) {
    return this.changeLine({ id: line.key, quantity: 0 })
      .then(() => this.addItems([replacement]))
      .catch((error) => {
        return this.restoreLine(line)
          .catch((restore_error) => {
            console.error("CartDrawer restore failed", restore_error);
          })
          .then(() => Promise.reject(error));
      });
  }

  restoreLine(line) {
    return this.findLine(line.key)
      .catch(() => this.addItems([{
        id: line.variant_id,
        quantity: line.quantity,
        properties: line.properties || {}
      }]));
  }

  changeLine(data) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: {
        'Content-Type': "application/json"
      },
      body: JSON.stringify(data)
    })
    .then((response) => {
      if (!response.ok) {
        return response.json().then((error) => Promise.reject(error));
      }

      return response.json();
    });
  }

  openUpsellChoice(detail, element) {
    const choice = element.closest(".cart-upsell-choice");
    const open = choice.classList.toggle("state--open");

    choice.querySelector(".cart-upsell-options").hidden = !open;
    element.setAttribute("aria-expanded", open);

    this.closeUpsellChoices(choice);
  }

  closeUpsellChoices(except) {
    this.querySelectorAll(".cart-upsell-choice.state--open").forEach((choice) => {
      if (choice === except) {
        return;
      }

      choice.classList.remove("state--open");
      choice.querySelector(".cart-upsell-options").hidden = true;
      choice.querySelector(".cart-upsell-add").setAttribute("aria-expanded", "false");
    });
  }

  addUpsellItem(detail, element) {
    return this.guard(() => this.addUpsell(detail, element));
  }

  addUpsell(detail, element) {
    if ((detail.quantity || 1) > this.roomLeft) {
      this.flashLimit();

      return Promise.resolve();
    }

    const card = element.closest(".cart-upsell-item");

    const option = element.closest(".cart-upsell-option");

    const button = option || (card ? card.querySelector(".cart-upsell-add") : element);

    button.classList.add("state--loading");
    button.setAttribute("aria-busy", "true");

    const item = Object.assign({}, detail, {
      properties: Object.assign({
        _attribution: "Product Discovery",
        _source: "Rebuy",
        _widget_id: "recess_custom_cart_widget"
      }, detail.properties || {})
    });

    return this.addItems([item])
      .then((response) => {
        document.dispatchEvent(new CustomEvent("cartdrawer:upsell", {
          detail: {
            items: response.items || [],
            quantity: detail.quantity
          }
        }));

        return this.syncCart();
      })
      .catch((error) => {
        button.classList.remove("state--loading");
        button.removeAttribute("aria-busy");

        if (this.handleCartError(error)) {
          return;
        }

        console.error("CartDrawer upsell failed", error);
      });
  }

  setBusy(element, busy) {
    if (!element) {
      return;
    }

    element.classList.toggle("state--busy", busy);
  }

  addItems(items) {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: {
        'Content-Type': "application/json"
      },
      body: JSON.stringify({ items: items })
    })
    .then((response) => {
      if (!response.ok) {
        return response.json().then((error) => Promise.reject(error));
      }

      return response.json();
    });
  }

  syncCart() {
    return fetch("/cart.js")
      .then((response) => response.json())
      .then((cart) => {
        document.dispatchEvent(new CustomEvent("cart:change", {
          detail: cart
        }));

        return this.render();
      });
  }

  change(data, element) {
    return this.guard(() => this.updateLine(data, element).catch(() => {}));
  }

  updateLine(data, element) {
    const line = element ? element.closest(".cart-line") : null;
    const current = line ? parseInt(line.dataset.quantity, 10) || 0 : 0;

    if (line && data.quantity - current > this.roomLeft) {
      this.flashLimit();

      return Promise.resolve();
    }

    this.setBusy(line, true);

    return this.changeLine(Object.assign({ sections: "cart-drawer" }, data))
      .then((cart) => {
        const sections = cart.sections;

        delete cart.sections;

        document.dispatchEvent(new CustomEvent("cart:change", {
          detail: cart
        }));

        return this.render(sections);
      })
      .catch((error) => {
        this.setBusy(line, false);

        if (!this.handleCartError(error)) {
          console.error("CartDrawer change failed", error);
        }

        throw error;
      });
  }

  render(sections) {
    function get_new_content(markup) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(markup, "text/html");

      return doc.getElementById("cart");
    }

    const markup = sections && sections["cart-drawer"]
      ? Promise.resolve(sections["cart-drawer"])
      : fetch("/?sections=cart-drawer")
        .then((response) => response.json())
        .then((response) => response["cart-drawer"]);

    return markup.then((string) => {
      const current = document.getElementById("cart");
      const fresh = typeof string === "string" ? get_new_content(string) : null;

      if (!current || !fresh) {
        return;
      }

      current.replaceWith(fresh);
      this.resetActionEvents();
      this.loadUpsells();
      this.replaceSubscriptionCopies();
    });
  }

  loadUpsells() {
    const container = this.querySelector('[data-element="upsells"]');

    if (!container) {
      return Promise.resolve();
    }

    const seed = container.dataset.seed;
    const limit = parseInt(container.dataset.limit, 10) || 0;

    if (container.dataset.curated === "true") {
      this.revealUpsells(container);

      return Promise.resolve();
    }

    if (!seed || limit < 1) {
      return Promise.resolve();
    }

    return this.fetchRecommendations(seed, limit, "complementary")
      .then((items) => {
        if (items.length >= limit) {
          return items;
        }

        return this.fetchRecommendations(seed, limit * 2, "related")
          .then((related) => items.concat(related));
      })
      .then((items) => this.renderUpsells(container, items))
      .catch((error) => {
        console.error("CartDrawer recommendations failed", error);
      });
  }

  fetchRecommendations(product_id, limit, intent) {
    const url = `/recommendations/products?section_id=cart-recommendations&product_id=${product_id}&limit=${limit}&intent=${intent}`;

    function clone(nodes) {
      return nodes.map((node) => node.cloneNode(true));
    }

    if (CartDrawer.recommendations.has(url)) {
      return Promise.resolve(clone(CartDrawer.recommendations.get(url)));
    }

    return fetch(url)
      .then((response) => response.text())
      .then((response) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(response, "text/html");
        const nodes = Array.from(doc.querySelectorAll(".cart-upsell-item"));

        CartDrawer.recommendations.set(url, nodes);

        return clone(nodes);
      });
  }

  renderUpsells(container, items) {
    const list = container.querySelector(".cart-upsells-list");
    const limit = parseInt(container.dataset.limit, 10) || 0;
    const seen = new Set((container.dataset.inCart || "").split(",").filter(Boolean));

    list.replaceChildren();

    items.forEach((item) => {
      const handle = item.dataset.productHandle;

      if (list.children.length >= limit || seen.has(handle)) {
        return;
      }

      seen.add(handle);
      list.appendChild(item);
    });

    this.revealUpsells(container);
  }

  revealUpsells(container) {
    const list = container.querySelector(".cart-upsells-list");

    container.hidden = list.children.length === 0;

    const scroll = container.querySelector("component-scroll");

    if (scroll && typeof scroll.refresh === "function") {
      scroll.refresh();
    }

    this.resetActionEvents();
  }
}

customElements.define("cart-drawer", CartDrawer);
